import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CognitoService } from 'src/app/auth/services/cognito.service';

export interface AgentUsage {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  estimatedCostUsd: number;
}

export interface AgentConversation {
  id: string;
  title: string | null;
  status: string;
  messageCount: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  createdAt: string;
  updatedAt: string;
}

export interface AgentMessage {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant' | 'tool_use' | 'tool_result';
  content: string;
  toolName: string | null;
  toolUseId: string | null;
  thinkingContent: string | null;
  stopReason: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  idempotencyKey: string | null;
  createdAt: string;
}

export type SSEEventType =
  | 'text_delta'
  | 'thinking'
  | 'tool_use'
  | 'tool_executing'
  | 'tool_result'
  | 'message_complete'
  | 'error';

export interface SSEEvent {
  type: SSEEventType | string;
  text?: string;
  name?: string;
  id?: string;
  message?: string;
  usage?: AgentUsage;
}

export interface AgentTaskStatus {
  found: boolean;
  taskId?: string;
  status?: 'queued' | 'running' | 'completed' | 'error';
  summary?: string;
  errorMessage?: string | null;
  createdAt?: string;
  finishedAt?: string | null;
  usage?: AgentUsage;
}

export interface SendMessageStreamOptions {
  onEvent: (event: SSEEvent) => void;
  onDone: () => void;
  onError: (error: AgentStreamError) => void;
  idempotencyKey?: string;
}

export interface AgentStreamError {
  message: string;
  status?: number;
  retryAfterSeconds?: number;
  rateLimited?: boolean;
}

export interface AgentAsyncResponse {
  taskId?: string;
  status?: string;
  conversationId?: string;
  message?: string;
  duplicate?: boolean;
}

const TOOL_LABELS: Record<string, string> = {
  get_transactions: 'Transacciones',
  get_current_rates: 'Tasas de cambio',
  get_user_balances: 'Saldos',
  get_user_info: 'Info de usuario',
  get_daily_operations: 'Operaciones diarias',
  get_platform_analytics: 'Analytics plataforma',
  get_reconciliation: 'Conciliacion',
  get_business_analytics: 'Analytics de negocio',
  get_stats_summary: 'Resumen estadistico',
  get_compliance_risk: 'Compliance y riesgo',
  get_payout_monitor: 'Monitor de pagos',
  get_crypto_analytics: 'Analytics cripto',
  get_user_lifecycle: 'Ciclo de vida',
  get_ves_corridor: 'Corredor Venezuela',
  get_bank_distribution: 'Distribucion bancaria',
};

@Injectable({ providedIn: 'root' })
export class AgentChatService {
  private readonly API = environment.apiNest + 'agent-control';

  constructor(
    private http: HttpClient,
    private cognito: CognitoService,
  ) {}

  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognito.getSession();
    if (!session?.idToken?.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }
    const token = session.idToken.jwtToken.trim();
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${token}`);
  }

  private handleError(error: HttpErrorResponse) {
    const message =
      error.error?.message || `Error del servidor: ${error.status}`;
    return throwError(() => ({ message, status: error.status, originalError: error }));
  }

  // -------- Conversations --------

  async createConversation(title?: string): Promise<AgentConversation> {
    return firstValueFrom(
      from(this.getHeaders()).pipe(
        switchMap((headers) =>
          this.http.post<AgentConversation>(
            `${this.API}/conversations`,
            { title },
            { headers },
          ),
        ),
        catchError(this.handleError),
      ),
    );
  }

  async listConversations(
    page = 1,
    limit = 20,
  ): Promise<{ data: AgentConversation[]; pagination: any }> {
    return firstValueFrom(
      from(this.getHeaders()).pipe(
        switchMap((headers) =>
          this.http.get<any>(`${this.API}/conversations`, {
            params: { page: String(page), limit: String(limit) },
            headers,
          }),
        ),
        catchError(this.handleError),
      ),
    );
  }

  async getConversation(
    id: string,
  ): Promise<{ conversation: AgentConversation; messages: AgentMessage[] }> {
    return firstValueFrom(
      from(this.getHeaders()).pipe(
        switchMap((headers) =>
          this.http.get<any>(`${this.API}/conversations/${id}`, { headers }),
        ),
        catchError(this.handleError),
      ),
    );
  }

  async archiveConversation(id: string): Promise<void> {
    await firstValueFrom(
      from(this.getHeaders()).pipe(
        switchMap((headers) =>
          this.http.delete(`${this.API}/conversations/${id}`, { headers }),
        ),
        catchError(this.handleError),
      ),
    );
  }

  // -------- Audit / Costs --------

  async getAuditLogs(page = 1, limit = 20): Promise<any> {
    return firstValueFrom(
      from(this.getHeaders()).pipe(
        switchMap((headers) =>
          this.http.get<any>(`${this.API}/audit`, {
            params: { page: String(page), limit: String(limit) },
            headers,
          }),
        ),
        catchError(this.handleError),
      ),
    );
  }

  async getCosts(dateFrom: string, dateTo: string): Promise<any> {
    return firstValueFrom(
      from(this.getHeaders()).pipe(
        switchMap((headers) =>
          this.http.get<any>(`${this.API}/costs`, {
            params: { dateFrom, dateTo },
            headers,
          }),
        ),
        catchError(this.handleError),
      ),
    );
  }

  // -------- Messages --------

  /**
   * Envía un mensaje via SSE streaming. Retorna AbortController para cancelar.
   * Si no se pasa idempotencyKey se genera automáticamente (evita duplicados en retry).
   */
  async sendMessageStreaming(
    conversationId: string,
    message: string,
    options: SendMessageStreamOptions,
  ): Promise<AbortController> {
    const controller = new AbortController();
    const idempotencyKey = options.idempotencyKey ?? this.generateIdempotencyKey();

    const session = await this.cognito.getSession();
    const token = session?.idToken?.jwtToken;

    try {
      // idempotencyKey viaja solo en el body para evitar headers custom
      // (mantener la whitelist de CORS del back estable).
      const response = await fetch(
        `${this.API}/conversations/${conversationId}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ message, idempotencyKey }),
          signal: controller.signal,
        },
      );

      if (!response.ok) {
        options.onError(await this.buildStreamError(response));
        return controller;
      }

      await this.consumeSSE(response, options);
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        options.onError({ message: err?.message || 'Error de conexión' });
      }
    }

    return controller;
  }

  async sendMessageAsync(
    conversationId: string,
    message: string,
    idempotencyKey?: string,
  ): Promise<AgentAsyncResponse> {
    const key = idempotencyKey ?? this.generateIdempotencyKey();
    return firstValueFrom(
      from(this.getHeaders()).pipe(
        switchMap((headers) =>
          this.http.post<AgentAsyncResponse>(
            `${this.API}/conversations/${conversationId}/messages/async`,
            { message, idempotencyKey: key },
            { headers },
          ),
        ),
        catchError(this.handleError),
      ),
    );
  }

  async getTaskStatus(conversationId: string, taskId: string): Promise<AgentTaskStatus> {
    return firstValueFrom(
      from(this.getHeaders()).pipe(
        switchMap((headers) =>
          this.http.get<AgentTaskStatus>(
            `${this.API}/conversations/${conversationId}/tasks/${taskId}`,
            { headers },
          ),
        ),
        catchError(this.handleError),
      ),
    );
  }

  // -------- Helpers --------

  formatToolName(name: string | null | undefined): string {
    if (!name) return '';
    return TOOL_LABELS[name] ?? name;
  }

  generateIdempotencyKey(): string {
    const c: any = (globalThis as any).crypto;
    if (c?.randomUUID) return c.randomUUID();
    // Fallback (RFC4122 v4-ish)
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
      const r = (Math.random() * 16) | 0;
      const v = ch === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private async buildStreamError(response: Response): Promise<AgentStreamError> {
    const retryAfterHeader = response.headers.get('Retry-After');
    const retryAfterSeconds = retryAfterHeader ? Number(retryAfterHeader) : undefined;

    let body = '';
    try {
      body = await response.text();
    } catch {}

    let parsed: any = null;
    try {
      parsed = body ? JSON.parse(body) : null;
    } catch {}

    const baseMessage =
      parsed?.message ||
      (response.status === 429
        ? 'Demasiadas solicitudes. Intenta en unos segundos.'
        : 'Error del servidor');

    return {
      message: baseMessage,
      status: response.status,
      retryAfterSeconds: Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : undefined,
      rateLimited: response.status === 429,
    };
  }

  /**
   * Parser SSE robusto: usa \n\n como delimitador de evento (spec), soporta
   * payloads JSON multilínea y maneja el sentinel [DONE].
   */
  private async consumeSSE(response: Response, options: SendMessageStreamOptions): Promise<void> {
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      let sepIndex: number;
      while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, sepIndex);
        buffer = buffer.slice(sepIndex + 2);

        const payload = this.extractSSEData(rawEvent);
        if (payload === null) continue;

        if (payload === '[DONE]') {
          options.onDone();
          return;
        }

        try {
          const event: SSEEvent = JSON.parse(payload);
          options.onEvent(event);
        } catch {
          // payload no-JSON: ignorar
        }
      }
    }

    options.onDone();
  }

  private extractSSEData(rawEvent: string): string | null {
    const dataLines: string[] = [];
    for (const line of rawEvent.split('\n')) {
      const trimmed = line.replace(/\r$/, '');
      if (trimmed.startsWith('data:')) {
        dataLines.push(trimmed.slice(5).replace(/^\s/, ''));
      }
    }
    if (dataLines.length === 0) return null;
    return dataLines.join('\n');
  }
}
